/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { Calendar, Clock, User, Plus, Trash2 } from "lucide-react";
import "./tasksnotes.css";

const STATUSES = ["Open", "In Progress", "Completed"];

const TasksNotes = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", dueDate: "", status: "Open", assignedTo: "" });
  const [filters, setFilters] = useState({ assignedTo: "" });

  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  useEffect(() => {
    api.get("/employees").then((res) => setEmployees(res.data)).catch(() => {});
    api.get("/tasks").then((res) => setTasks(res.data)).catch(() => {});
  }, []);

  // Filter assignable employees strictly based on user's role & team
  const assignableEmployees = useMemo(() => {
    if (!user) return [];
    if (user.role === "Admin") {
      return employees.filter(e => e.isActive);
    }
    if (user.role === "Manager") {
      // Manager can assign to themselves or any agent reporting directly to them
      return employees.filter(e => e.isActive && (String(e.id) === String(user.id) || String(e.reportingTo) === String(user.id)));
    }
    // Agent can only assign to themselves
    return employees.filter(e => String(e.id) === String(user.id));
  }, [employees, user]);

  const getEmpName = (idOrName) => {
    if (!idOrName) return "Unassigned";
    if (isNaN(Number(idOrName))) return idOrName;
    const emp = employees.find(e => String(e.id || e._id) === String(idOrName));
    return emp ? emp.name : `User #${idOrName}`;
  };

  const handleOpenAddModal = () => {
    setNewTask({
      title: "",
      description: "",
      dueDate: "",
      status: "Open",
      assignedTo: user?.id || ""
    });
    setShowModal(true);
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !newTask.assignedTo) { toast.error("Title and Assign To required."); return; }
    try {
      const { data } = await api.post("/tasks", { ...newTask, title: newTask.title.trim(), description: (newTask.description || "").trim() });
      setTasks((prev) => [data, ...(prev || [])]);
      setShowModal(false);
      setNewTask({ title: "", description: "", dueDate: "", status: "Open", assignedTo: "" });
      toast.success("Task added!");
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to add task");
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      setTasks((prev) => prev.filter((t) => (t.id || t._id) !== id));
      toast.success("Task deleted");
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.id || task._id}`, { status: newStatus });
      setTasks(prev => prev.map(t => (t.id || t._id) === (task.id || task._id) ? { ...t, status: newStatus } : t));
      toast.success(`Task moved to ${newStatus}`);
    } catch(err) {
      toast.error("Failed to update status");
    }
  };

  const visible = useMemo(() => tasks.filter((t) => {
    const aMatch = filters.assignedTo ? String(t.assignedTo) === String(filters.assignedTo) : true;
    return aMatch;
  }), [tasks, filters]);

  const isOverdue = (dueDate, status) => {
    if (status === "Completed" || !dueDate) return false;
    return new Date(dueDate) < new Date(new Date().setHours(0,0,0,0));
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
    setTimeout(() => {
      e.target.classList.add("is-dragging");
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("is-dragging");
    setDraggedTask(null);
    setDragOverCol(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    setDragOverCol(status);
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    if (draggedTask && draggedTask.status !== newStatus) {
      handleStatusChange(draggedTask, newStatus);
    }
  };

  return (
    <div className="tasks-page">
      {/* ── Page Toolbar: Count, Filter & Actions ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
            {visible.length} Active Tasks
          </span>
          <span className="text-muted small d-none d-md-inline">Kanban task stages & team assignments</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          {assignableEmployees.length > 1 && (
            <select
              className="leads-filter-select"
              value={filters.assignedTo}
              onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
              style={{ minWidth: 160 }}
            >
              <option value="">All Team Assignees</option>
              {assignableEmployees.map((a) => (
                <option key={a.id || a._id} value={a.id || a._id}>{a.name} ({a.role})</option>
              ))}
            </select>
          )}

          <button className="leads-btn primary" onClick={handleOpenAddModal}>
            <Plus size={14} /> Add Task
          </button>
        </div>
      </div>

      {/* ── Kanban Board ── */}
      <div className="kanban-board">
        {STATUSES.map(status => {
          const colTasks = visible.filter(t => t.status === status);
          return (
            <div 
              key={status} 
              className={`kanban-col ${dragOverCol === status ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, status)}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => handleDrop(e, status)}
            >
              <div className="kanban-col-header">
                <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '13px' }}>{status}</span>
                <span className="count-badge">{colTasks.length}</span>
              </div>
              <div className="kanban-col-body">
                {colTasks.length === 0 ? (
                  <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                    No tasks in {status}
                  </div>
                ) : (
                  colTasks.map(t => {
                    const taskId = t.id || t._id;
                    const overdue = isOverdue(t.dueDate, t.status);
                    return (
                      <div 
                        key={taskId} 
                        className={`kanban-card ${overdue ? 'overdue' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, t)}
                        onDragEnd={handleDragEnd}
                      >
                        <h4 className="kanban-card-title">{t.title}</h4>
                        {t.description && <p className="kanban-card-desc">{t.description}</p>}

                        <div className="kanban-card-footer">
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <User size={12} /> {getEmpName(t.assignedTo)}
                          </span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="quick-act-btn border-0 bg-transparent text-muted"
                              title="Delete task"
                              onClick={(e) => { e.stopPropagation(); handleDeleteTask(taskId); }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Task Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName="paj-clean-modal">
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Task Title *</Form.Label>
            <Form.Control
              type="text"
              value={newTask.title}
              onChange={(e) => setNewTask(p => ({ ...p, title: e.target.value }))}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask(p => ({ ...p, description: e.target.value }))}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Assign To *</Form.Label>
            <Form.Select
              value={newTask.assignedTo}
              onChange={(e) => setNewTask(p => ({ ...p, assignedTo: e.target.value }))}
            >
              <option value="">Select Assignee...</option>
              {assignableEmployees.map(a => (
                <option key={a.id || a._id} value={a.id || a._id}>
                  {a.name} ({a.role})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddTask}>Create Task</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TasksNotes;
